import React, { FormEvent, MouseEvent, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { CurrentUser } from "../../../../stores/CurrentUserStore/Types";
import { putRequest } from "../../../../utils/httpRequest";

interface NotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: CurrentUser;
  getCurrentUser: () => Promise<void>;
}

const NotificationPreferencesModal = ({
  isOpen,
  onClose,
  currentUser,
  getCurrentUser,
}: NotificationPreferencesModalProps) => {
  const [repliesEnabled, setRepliesEnabled] = useState(
    currentUser.community_alerts_enabled,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setRepliesEnabled(currentUser.community_alerts_enabled);
    setIsSaved(false);
    setError("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen, onClose]);

  const closeFromBackdrop = (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const savePreferences = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSaving(true);
    setIsSaved(false);
    setError("");

    try {
      await putRequest(`/api/users/${currentUser.id}.json`, {
        user: { community_alerts_enabled: repliesEnabled },
      });
      await getCurrentUser();
      setIsSaved(true);
    } catch (requestError) {
      const axiosError = requestError as AxiosError;
      setError(axiosError.message || "Unable to save notification settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <aside
      className={`notif-prefs-modal ${isOpen ? "is-open" : ""}`}
      id="notifPrefsModal"
      aria-modal="true"
      role="dialog"
      aria-labelledby="notif-prefs-title"
      hidden={!isOpen}
      onClick={closeFromBackdrop}
    >
      <div className="notif-prefs-card">
        <button
          className="notif-prefs-close"
          type="button"
          id="notifPrefsClose"
          aria-label="Close notification settings"
          onClick={onClose}
        >
          &times;
        </button>
        <div className="notif-prefs-eyebrow">Notification settings</div>
        <h2 id="notif-prefs-title">
          Tell us how loud <em>you'd like us to be</em>
        </h2>
        <p className="prefs-sub">
          Choose whether Community activity appears in your notifications.
        </p>

        <form id="notifPrefsForm" onSubmit={savePreferences}>
          <div className="pref-row">
            <div className="pref-label">
              <span className="pref-title">Replies and comments</span>
              <span className="pref-help">
                Notify me when someone replies, comments on, or likes my
                Community activity.
              </span>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                name="replies"
                checked={repliesEnabled}
                disabled={isSaving}
                onChange={(event) => {
                  setRepliesEnabled(event.target.checked);
                  setIsSaved(false);
                  setError("");
                }}
              />
              <span className="slider" aria-hidden="true" />
              <span className="sr-only">
                Notify me about replies, comments, and likes
              </span>
            </label>
          </div>

          <div className="notif-prefs-foot">
            <span className="save-note">
              {error || (isSaved ? "Notification settings saved." : "")}
            </span>
            <button
              type="submit"
              className="btn-prefs-save"
              disabled={isSaving}
              style={{ background: isSaved ? "#1f5538" : undefined }}
            >
              {isSaving
                ? "Saving..."
                : isSaved
                  ? "Saved ✓"
                  : "Save preferences"}
            </button>
          </div>
        </form>
      </div>
    </aside>
  );
};

export default NotificationPreferencesModal;
